<template>
  <div
    class="d-flex flex-column"
    style="height: 70vh;"
  >
    <div class="teal dark:gray lighten-4 py-6">
      <v-container fluid>
        <v-row>
          <v-col
            cols="6"
            offset="3"
          >
            <v-text-field
              v-model="searchTerm"
              autofocus
              color="secondary"
              :hide-details="!showSearchHint"
              hint="At least three characters are required to perform search"
              label="Search terms"
              outlined
              :persistent-hint="showSearchHint"
              prepend-inner-icon="mdi-account-search"
            />
            <div>
              <v-switch
                v-model="hideInvalidEntries"
                class="mt-2 mx-2"
                color="secondary"
                dense
                hide-details
                inset
                label="Hide entries that cannot be imported"
              />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <div
      v-if="isSearching"
      class="grow"
    >
      <div
        class="d-flex align-center justify-center"
        style="height: 100%"
      >
        <v-progress-circular
          color="primary"
          indeterminate
          :size="128"
          :width="8"
        />
      </div>
    </div>

    <div
      v-else-if="searchTerm.length < 3"
      class="grow"
    >
      <div
        class="d-flex align-center justify-center text-overline"
        style="height: 100%;"
      >
        Please use the above box to search for OWR Navigations.
      </div>
    </div>

    <div
      v-else-if="!filteredResults.length"
      class="grow"
    >
      <div
        class="d-flex flex-column align-center justify-center"
        style="height: 100%;"
      >
        <SadSearchIcon width="168" />
        <div class="pt-16 text-overline">
          There are no results matching the provided search terms.
        </div>
      </div>
    </div>

    <div
      v-else
      class="flex-grow-1"
      style="min-height: 0;"
    >
      <div style="height: 100%; overflow: auto;">
        <v-card
          v-for="(item, index) of filteredResults"
          :key="index"
          class="ma-8"
          :class="isValidStatus(item.status) ? 'active-item' : ''"
          :disabled="!isValidStatus(item.status)"
          hover
          outlined
          @click="_ => selectEntry(item.id)"
        >
          <v-card-title class="d-flex align-center">
            <span class="mr-4">
              {{ item.first_name }} {{ item.last_name }}
            </span>
            <v-chip
              :color="isValidStatus(item.status) ? 'primary' : ''"
              label
              small
            >
              {{ item.status }}
            </v-chip>
            <v-spacer />
            <span class="font-weight-regular">
              #{{ item.id }}
            </span>
          </v-card-title>

          <div class="d-flex align-center pa-4 pt-0">
            <span class="d-flex align-center pr-4">
              <v-icon
                left
                small
              >
                mdi-cake-variant
              </v-icon>
              {{ item.birth_date | formatDate }}
            </span>

            <span class="d-flex align-center pr-4">
              <v-icon left>
                mdi-earth
              </v-icon>
              {{ item.birth_country | notSupplied }}
            </span>

            <span class="d-flex align-center pr-4">
              A# {{ item.a_num | notSupplied }}
            </span>

            <span class="d-flex align-center pr-4">
              <v-icon left>
                mdi-account-group
              </v-icon>
              {{ item.derivatives }}
            </span>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapActions } from 'vuex'

import { alertHandler } from '@/alertHandler'
import { parseErrorResponseMessage } from '@/utilities/error-responses'

import { INTEGRATION_NAMESPACE } from '@/store/integrations'

import SadSearchIcon from '@/components/Icons/SadSearchIcon.vue'

export default {
  name: 'LegalCaseEditorImportPartySearch',

  components: {
    SadSearchIcon,
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      hideInvalidEntries: false,
      isSearching: false,
      searchResults: [],
      searchTerm: '',
    }
  },

  computed: {
    filteredResults () {
      if (!this.hideInvalidEntries) return (this.searchResults || [])

      return (this.searchResults || []).filter(entry => this.isValidStatus(entry.status))
    },

    showSearchHint () {
      return this.searchTerm && this.searchTerm.length < 3
    },
  },

  watch: {
    searchTerm () {
      if (this.searchTerm.length > 2) {
        this.isSearching = true
        this.debounceSearch()
      }
    },
  },

  methods: {
    debounceSearch: debounce(function () { this.loadOWRAvailableInterviews() }, 250),

    isValidStatus (status) {
      return ['eligible', 'approved', 'processed', 'paid'].includes(status?.toLowerCase())
    },

    loadOWRAvailableInterviews () {
      this.isSearching = true

      this.searchOWRNavigations({ searchTerm: this.searchTerm }).
        then(results => { this.searchResults = results }).
        catch(error => this.displayAlert('error', parseErrorResponseMessage(error))).
        finally(_ => { this.isSearching = false })
    },

    selectEntry (id) {
      this.$emit('on-select', id)
    },

    ...mapActions(INTEGRATION_NAMESPACE, [
      'searchOWRNavigations',
    ]),
  },
}
</script>

<style scoped>
.active-item {
  border-color: rgb(var(--v-success-rgb));
  background-color: rgba(var(--v-success-rgb), 0.1);
  transition: background-color .4s cubic-bezier(.25, .8, .25, 1),
              border-shadow .4s cubic-bezier(.25, .8, .25, 1),
              box-shadow .4s cubic-bezier(.25, .8, .25, 1);
}

.active-item:hover {
  background-color: rgba(var(--v-success-rgb), 0.15);
  box-shadow: 0px 5px 5px -3px rgba(var(--v-success-rgb), 0.08),
              0px 8px 10px 1px rgba(var(--v-success-rgb), 0.06),
              0px 3px 14px 2px rgba(var(--v-success-rgb), 0.05);
}
</style>
